import { FieldValues } from "react-hook-form";
import { ControllerRenderProps } from "react-hook-form";
import { Upload, File, X } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/class-control";

interface InputFileProps<T extends FieldValues> {
  field: ControllerRenderProps<T>
  placeholder?: string
  accept?: string
  maxSize?: number
  disabled?: boolean
  onFileSelect?: (file: File | null) => void
  onInvalidFile?: (file: File) => void
  existingUrl?: string
}

export function InputFile<T extends FieldValues>({
  field,
  placeholder,
  accept = ".pdf",
  maxSize,
  disabled = false,
  onFileSelect,
  onInvalidFile,
  existingUrl,
}: InputFileProps<T>) {
  const [selectedFile, setSelectedFile] = useState<File | null>(field.value || null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    const file = e.target.files?.[0] || null;

    if (file && maxSize && file.size > maxSize) {
      onInvalidFile?.(file);
      setSelectedFile(null);
      field.onChange(null);
      const input = document.getElementById(field.name) as HTMLInputElement;
      if (input) input.value = '';
      return;
    }

    setSelectedFile(file);
    field.onChange(file);
    onFileSelect?.(file);
  };

  const handleRemoveFile = () => {
    if (disabled) return;
    setSelectedFile(null);
    field.onChange(null);
    // Reset the input value
    const input = document.getElementById(field.name) as HTMLInputElement;
    if (input) input.value = '';
  };

  const existingFileName = existingUrl && typeof existingUrl === "string" ? existingUrl.split('/').pop() || 'documento.pdf' : null

  const Start = () => {
    if (selectedFile) {
      return (
        <div className="flex items-center gap-2 flex-1">
          <File className="w-4 h-4 text-green-400" />
          <span className="text-sm text-white truncate flex-1">{selectedFile.name}</span>
          <button
            type="button"
            onClick={handleRemoveFile}
            className="text-gray-400 hover:text-red-400 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      );
    }

    if (existingFileName) {
      return (
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <File className="w-4 h-4 text-blue-400 shrink-0" />
          <span className="text-sm text-white truncate flex-1 min-w-0">{existingFileName}</span>
          <label
            htmlFor={field.name}
            className={cn("cursor-pointer text-gray-400 hover:text-white transition-colors", disabled && "cursor-not-allowed")}
            title="Substituir documento"
          >
            <Upload className="w-4 h-4" />
          </label>
        </div>
      );
    }

    return (
      <div className="flex gap-4">
        <label
          htmlFor={field.name}
          className={cn("cursor-pointer text-sm", disabled && "cursor-not-allowed")}
        >
          <Upload className="w-4 h-4 mx-auto text-gray-400 hover:text-white" />
        </label>
        <span className="text-sm text-gray-400">{placeholder ? placeholder : "Faça upload (JPG, PDF, PNG)"}</span>
      </div>
    );
  };

  return (
    <div className={cn(
      "flex items-center w-full px-3 py-2 rounded-md bg-[#15203c] text-white focus-within:ring-1 focus-within:ring-primary",
      (selectedFile || existingFileName) ? "border-2 border-[#7846ff]" : "border border-gray-600",
      disabled && "opacity-60 cursor-not-allowed"
    )}>
      <Start />
      <input
        type="file"
        accept={accept}
        name={field.name}
        onChange={handleFileChange}
        className="hidden"
        id={field.name}
        ref={field.ref}
        disabled={disabled}
      />
    </div>
  );
}